/**
 * Renders an article's ISO date the way a reader in `locale` would write it.
 *
 * Frontmatter dates were being printed raw — "2026-05-29" next to the author on
 * every card. Intl gives the reader's own conventions for free, which matters on a
 * site that ships in 56 languages: the same page should not show a German visitor
 * an ISO string.
 *
 * Returns the input untouched if it is not a date we can parse. A malformed
 * frontmatter date is a content bug, and showing it is how someone notices; throwing
 * would take the whole index down with it.
 */
export function formatArticleDate(date: string, locale: string): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  } catch {
    // An unsupported locale tag should not cost the reader the date.
    return parsed.toISOString().slice(0, 10);
  }
}
